import React from 'react';
import {
  Card,
  Typography,
  Button,
  Container,
  Grid,
  Box,
  Chip
} from '@mui/material';
import {
  Download,
  CreditCard,
  LocationOn,
  QrCode,
  ArrowForward,
  Smartphone
} from '@mui/icons-material';

const steps = [
  {
    number: "01",
    icon: Download,
    title: "Baixe o App",
    description: "Faça o download gratuito na App Store ou Google Play e crie sua conta em segundos.",
    color: "#3b82f6"
  },
  {
    number: "02",
    icon: CreditCard,
    title: "Escolha seu Plano",
    description: "Selecione o plano ideal para seu tipo de carro e frequência de uso. Cancelamento livre.",
    color: "#10b981"
  },
  {
    number: "03",
    icon: LocationOn,
    title: "Encontre um Parceiro",
    description: "Use o mapa integrado para localizar o lava-rápido mais próximo da sua localização.",
    color: "#f59e0b"
  },
  {
    number: "04",
    icon: QrCode,
    title: "Valide com QR Code",
    description: "Chegue ao local, escaneie o QR Code e pronto! Seu carro será lavado com qualidade garantida.",
    color: "#8b5cf6"
  }
];

export const HowItWorksSection = () => {
  return (
    <section id="como-funciona" className="section-spacing bg-muted">
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }} className="component-spacing">
          <Chip
            icon={<QrCode />}
            label="PROCESSO SIMPLES"
            sx={{
              backgroundColor: 'rgba(30, 58, 138, 0.1)',
              color: '#1e3a8a',
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography
            variant="h2"
            className="text-section"
            sx={{
              color: '#1a202c',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              fontWeight: 700
            }}
          >
            Como funciona o WashPass
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#64748b',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Em apenas 4 passos simples, você terá acesso a lavagens ilimitadas
            para seu carro. Veja como é fácil:
          </Typography>
        </Box>

        {/* Steps */}
        <Box sx={{ position: 'relative' }}>
          {/* Connection Line - Hidden on mobile */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #06b6d4, #1e3a8a, #06b6d4)',
              transform: 'translateY(-50%)',
              zIndex: 0
            }}
          />

          <Grid container spacing={3} sx={{ position: 'relative', zIndex: 10, alignItems: 'stretch' }}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Grid item xs={12} md={6} lg={3} key={index} sx={{ display: 'flex' }}>
                  <Card
                    sx={{
                      p: '30px',
                      border: 'none',
                      boxShadow: '0 4px 20px rgba(30, 58, 138, 0.1)',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                      transition: 'all 0.3s ease',
                      borderRadius: '10px',
                      position: 'relative',
                      flex: 1, // Ocupa toda a altura disponível do Grid item
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible', // Permite que elementos absolutos saiam do card
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(30, 58, 138, 0.15)',
                        transform: 'translateY(-8px)'
                      }
                    }}
                  >
                    {/* Step Number */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -16,
                        left: 24,
                        width: 32,
                        height: 32,
                        backgroundColor: '#06b6d4',
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
                        zIndex: 10
                      }}
                    >
                      {step.number}
                    </Box>

                    <Box sx={{ textAlign: 'center', pt: 4, height: '100%', display: 'flex', flexDirection: 'column', paddingTop: '20px' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          backgroundColor: 'rgba(6, 182, 212, 0.1)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#06b6d4',
                            transform: 'scale(1.1)',
                            '& .MuiSvgIcon-root': {
                              color: 'white'
                            }
                          }
                        }}
                      >
                        <Icon
                          sx={{
                            color: '#06b6d4',
                            width: 32,
                            height: 32,
                            transition: 'color 0.3s ease'
                          }}
                        />
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: '#1a202c',
                          mb: 1.5
                        }}
                      >
                        {step.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#64748b',
                          lineHeight: 1.6,
                          flex: 1
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>

                    {/* Arrow for desktop */}
                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          display: { xs: 'none', lg: 'block' },
                          position: 'absolute',
                          right: -16,
                          top: '46%',
                          transform: 'translateY(-50%)',
                          zIndex: 20
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          <ArrowForward sx={{ color: '#06b6d4', width: 16, height: 16 }} />
                        </Box>
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#1a202c',
              mb: 2
            }}
          >
            Pronto para começar?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#64748b',
              mb: 3,
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            Baixe o app agora e tenha acesso imediato a toda nossa rede de parceiros.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Smartphone />}
            href="/app-download"
            sx={{
              backgroundColor: '#06b6d4',
              color: 'white',
              fontWeight: 600,
              px: 4,
              py: 2,
              height: 'auto',
              boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
              '&:hover': {
                backgroundColor: '#0891b2',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(6, 182, 212, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Baixar App Grátis
          </Button>
        </Box>
      </Container>
    </section>
  );
};
